import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { fetchLegislationDocumentByCelex } from '@/shared/hooks/api/radarScopeTier'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { ThemeSwitch } from '@/components/theme-switch'
import { Button } from '@/components/ui/button'
import { LegislationViewerModal } from './components/LegislationViewerModal'
import {
  buildEurLexUrlFromCelex,
  parseLegislationCitationRouteParams,
  toLegislationCitationTargetFromCelexLookup,
} from './components/llmChatToolOutput'
import { llmChatBasePath } from './llmChatPaths'

export function LegislationCitationPage() {
  const { t } = useTranslation('llmChat')
  const navigate = useNavigate()
  const params = useParams<{
    celex?: string
    startBlockId?: string
    endBlockId?: string
  }>()
  const parsed = parseLegislationCitationRouteParams(params)
  const celexQuery = useQuery({
    queryKey: [
      'radar-scope-tier',
      'legislation-document-by-celex',
      parsed?.celex ?? null,
    ],
    queryFn: () => {
      if (!parsed) {
        return null
      }
      return fetchLegislationDocumentByCelex(parsed.celex)
    },
    enabled: parsed != null,
  })

  const target = useMemo(() => {
    if (!parsed || !celexQuery.data) {
      return null
    }
    return toLegislationCitationTargetFromCelexLookup({
      celex: celexQuery.data.celex,
      documentId: celexQuery.data.documentId,
      startBlockId: parsed.startBlockId,
      endBlockId: parsed.endBlockId,
    })
  }, [celexQuery.data, parsed])

  const eurLexUrl = parsed ? buildEurLexUrlFromCelex(parsed.celex) : null
  const showLookupError =
    parsed == null ||
    celexQuery.isError ||
    (celexQuery.isSuccess && celexQuery.data == null)

  return (
    <>
      <Header fixed>
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>
      <Main className='flex flex-1 flex-col items-center justify-center gap-3'>
        {celexQuery.isLoading ? (
          <div className='flex items-center text-muted-foreground'>
            <Loader2 className='size-5 animate-spin' />
            <span className='ms-2 text-sm'>{t('legislationViewer.loading')}</span>
          </div>
        ) : null}
        {showLookupError && !celexQuery.isLoading ? (
          <>
            <p className='text-sm text-muted-foreground'>
              {t('legislationViewer.loadError')}
            </p>
            {eurLexUrl ? (
              <Button type='button' variant='outline' size='sm' asChild>
                <a href={eurLexUrl} target='_blank' rel='noreferrer noopener'>
                  {t('legislationViewer.openOnEurLex')}
                </a>
              </Button>
            ) : null}
          </>
        ) : null}
      </Main>
      <LegislationViewerModal
        open={target != null}
        target={target}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            navigate(llmChatBasePath())
          }
        }}
      />
    </>
  )
}
